import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '~/lib/utils';


const buttonVariants = cva(
  'inline-flex items-center cursor-pointer w-fit justify-center gap-2.5 whitespace-nowrap text-sm font-medium transition-all duration-300 ease-in-out disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*="size-"])]:size-3.5 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive capitalize ',
  {
    variants: {
      variant: {
        default:
          'bg- text-white hover:bg-blue-primary backdrop-blur-[5px] border border-white/16 gap-[10px]',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-black/32 text-white hover:bg-blue-primary backdrop-blur-[4px] border-0 hover:border hover:border-white/16 hover:backdrop-blur-[5px]',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        glass:
          'bg-white/12 text-white hover:bg-blue-primary backdrop-blur-[4px] border border-transparent hover:border-white/16 hover:backdrop-blur-[5px]',
        tab:
          'px-[10px] py-[5px] uppercase text-white hover:text-white/40 bg-transparent border-0 text-[17px] tracking-[0.17px] font-medium',
        'text-link':
          'px-0 py-1 gap-1.5 text-white hover:underline hover:decoration-white decoration-transparent underline-offset-2 transition-all text-[15px] normal-case font-normal',
        icon:
          'p-0 bg-transparent border-0 hover:opacity-40 transition-opacity cursor-pointer gap-0 normal-case focus-visible:ring-0 focus-visible:border-0 [&_img]:w-full [&_img]:h-full [&_svg]:w-full [&_svg]:h-full',
      },
      size: {
        sm: 'px-5 py-2 text-xs text-[13px]',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
        'icon-30': 'size-[30px]',
        'icon-34': 'size-[34px]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Button = ({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
};

export { Button, buttonVariants };
